import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { LoginStep } from "./components/LoginStep";
import { LoginFormData, LoginFormSteps } from "./components/steps/StepModel";
import {
  selectCurrentStep,
  selectLoading,
  decrementCurrentStep,
  selectStatus,
  selectUserValidated,
  invalidateUser,
} from "./components/LoginPageSlice";
import { EmailAddressSubmit } from "./components/steps/EmailAddressStep";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { PasswordSubmit } from "./components/steps/PasswordStep";
import { TwoFactorAuthSubmit } from "./components/steps/TwoFactorAuthStep";
import { Link, useParams } from "react-router-dom";
import {
  getLoginScreenNarrative,
  selectCompanyBranding,
  selectLoginScreenNarrative,
} from "app/modules/system/whiteLabelling/CompanyBrandingSlice";
import { useCallback, useEffect } from "react";
import { actions } from "../redux/AuthRedux";
import { selectCompanyId } from "app/modules/system/whiteLabelling/CompanyProviderSlice";
import { sanitizeHtml } from "cpl-react-common/Helpers/HtmlSanitizerHelpers";

const initialValues: LoginFormData = {
  emailAddress: "",
  password: "",
  oneTimePassword: "",
};

const validationSchema = {
  [LoginFormSteps.EmailAddress]: Yup.object().shape({
    emailAddress: Yup.string().email("Wrong email format").required("Email address is required"),
  }),
  [LoginFormSteps.Password]: Yup.object().shape({
    password: Yup.string().required("Password is required"),
  }),
};

const submitFunctions = {
  [LoginFormSteps.EmailAddress]: EmailAddressSubmit,
  [LoginFormSteps.Password]: PasswordSubmit,
  [LoginFormSteps.TwoFactorAuth]: TwoFactorAuthSubmit,
};

export const LoginPage = () => {
  const userAlreadyValidated = useSelector(selectUserValidated);
  const loading = useSelector(selectLoading);
  const currentStep = useSelector(selectCurrentStep);
  const status = useSelector(selectStatus);
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();
  const companyBranding = useSelector(selectCompanyBranding);
  const { emailAddress } = useParams();
  const loginScreenNarrative = useSelector(selectLoginScreenNarrative);
  const companyID = useSelector(selectCompanyId);

  useEffect(() => {
    thunkDispatch(getLoginScreenNarrative(companyID!));
  }, [companyID, thunkDispatch]);

  const onInputChanged = useCallback(() => {
    if (userAlreadyValidated) {
      dispatch(invalidateUser());
    }
  }, [dispatch, userAlreadyValidated]);

  const getExtraButtons = (formikProps) => {
    if (currentStep > 0 && currentStep < LoginFormSteps.TwoFactorAuth) {
      return (
        <button
          type="button"
          onClick={() => dispatch(decrementCurrentStep())}
          className="cpl-btn btn-lg cpl-btn-secondary w-100 mb-5"
          disabled={loading}
          data-test-id="login-form__back-button"
        >
          <span className="indicator-label">Back</span>
        </button>
      );
    }

    if (currentStep == LoginFormSteps.TwoFactorAuth) {
      return (
        <button
          type="button"
          onClick={() => dispatch(actions.logout())}
          className="cpl-btn btn-lg cpl-btn-danger w-100 mb-5 text-white"
          disabled={loading}
          data-test-id="login-form__logout-button"
        >
          <span className="indicator-label">Logout</span>
        </button>
      );
    }

    if (currentStep === 0) {
      return (
        <Link
          to={`/auth/registration/${formikProps.values.emailAddress}`}
          className="cpl-link cpl-text-primary fw-600 d-inline-block fs-7 mt-0 mb-5"
          data-test-id="login-form__register-link"
        >
          Register
        </Link>
      );
    }
  };

  return (
    <Formik
      initialValues={{ ...initialValues, emailAddress: emailAddress ?? initialValues.emailAddress }}
      validationSchema={validationSchema[currentStep]}
      onSubmit={(values: LoginFormData, helpers: FormikHelpers<LoginFormData>) => {
        submitFunctions[currentStep](values, dispatch, thunkDispatch);
        helpers.setTouched({});
      }}
    >
      {(formikProps) => (
        <Form autoComplete="on">
          {/* begin::Heading */}
          <div className="text-center mb-10">
            <h1 className="heading-text cpl-text-primary mb-3">{`Sign In To ${
              companyBranding?.clientPortalName ?? "CPL Client Portal"
            }`}</h1>
          </div>
          {/* begin::Heading */}
          {status && (
            <div className="mb-lg-6 alert alert-danger">
              <div className="alert-text font-weight-bold">{status}</div>
            </div>
          )}
          <LoginStep currentStep={currentStep} formikProps={formikProps} onInputChange={onInputChanged} />
          {/* begin::Action */}
          <div className="text-center">
            <button
              type="submit"
              id="kt_sign_in_submit"
              className="cpl-btn btn-lg cpl-btn-primary w-100 mb-3"
              disabled={loading}
              data-test-id="login-form__submit-button"
            >
              {loading ? (
                <span className="indicator-progress" style={{ display: "block" }}>
                  Please wait...
                  <span className="spinner-border spinner-border-sm align-middle ms-2" />
                </span>
              ) : (
                <span className="indicator-label">Continue</span>
              )}
            </button>
            {getExtraButtons(formikProps)}
          </div>
          <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(loginScreenNarrative?.pageDescription) }}></div>
          <a
            type="button"
            className="cpl-text-primary text-decoration-none"
            href="/accessibility"
            target="_blank"
            data-test-id="login-form__accessibility-statement-link"
          >
            You can read our Accessibility Statement here
          </a>
          {/* end::Action */}
        </Form>
      )}
    </Formik>
  );
};
